import React, { useEffect, useState, useMemo } from 'react';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiDownload, FiFileText, FiFile } from 'react-icons/fi';
import Icon from '../../../../components/Icon';
import AddNewLocationSurveyModal from '../components/AddNewLocationSurveyModal';
import NewLocationSurveyDetailsModal from '../components/NewLocationSurveyDetailsModal';
import { subscribeToNewLocationSurveys, type NewLocationSurvey } from '../service/newLocationSurveyService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Table, { Column } from '../../../../components/Table';

export default function NewLocationSurveys() {
    const [surveys, setSurveys] = useState<NewLocationSurvey[]>([]);
    const [filtered, setFiltered] = useState<NewLocationSurvey[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedSurvey, setSelectedSurvey] = useState<NewLocationSurvey | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsAutoEdit, setDetailsAutoEdit] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const psoProfile = useAppSelector((s) => s.user.pso);
    const designation = psoProfile?.designation || '';
    const normalizedDesignation = useMemo(() => designation.replace(/_/g, ' ').toLowerCase(), [designation]);
    const isMIS = useMemo(() => normalizedDesignation.includes('mis'), [normalizedDesignation]);
    const isRegional = useMemo(() => normalizedDesignation.includes('regional pso'), [normalizedDesignation]);

    useEffect(() => {
        const unsubscribe = subscribeToNewLocationSurveys((surveysData) => {
            setSurveys(surveysData);
            setFiltered(surveysData);
        });

        return () => {
            unsubscribe?.();
        };
    }, []);

    const handleSurveyAdded = (message?: string) => {
        showToast(message || 'Survey saved successfully', 'success');
    };

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        let list = surveys.filter((survey) => {
            if (!isRegional) return true;
            const status = (survey.status || '').toLowerCase();
            return ['submitted', 'approved', 'rejected', 'cancelled', 'completed'].includes(status);
        });

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(survey => {
                const surveyId = (survey.surveyId || '').toString().toLowerCase();
                const location = (survey.locationAddress || '').toString().toLowerCase();
                const surveyDate = (survey.surveyDate || '').toString().toLowerCase();
                const requestDate = (survey.surveyRequestDate || '').toString().toLowerCase();
                return surveyId.includes(term) || location.includes(term) || surveyDate.includes(term) || requestDate.includes(term);
            });
        }

        // Apply date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            list = list.filter(survey => {
                const surveyDate = new Date(survey.surveyDate);
                const surveyDateOnly = new Date(surveyDate.getFullYear(), surveyDate.getMonth(), surveyDate.getDate());

                switch (dateFilter) {
                    case 'daily':
                        return surveyDateOnly.getTime() === today.getTime();
                    case 'weekly':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        return surveyDateOnly >= weekAgo && surveyDateOnly <= today;
                    case 'monthly':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        return surveyDateOnly >= monthAgo && surveyDateOnly <= today;
                    default:
                        return true;
                }
            });
        }

        // Sort by survey date (newest first)
        list.sort((a, b) => {
            const dateA = new Date(a.surveyDate);
            const dateB = new Date(b.surveyDate);
            return dateB.getTime() - dateA.getTime();
        });

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, surveys, dateFilter]);

    const openDetailsModal = (survey: NewLocationSurvey, autoEdit = false) => {
        setSelectedSurvey(survey);
        setDetailsAutoEdit(autoEdit);
        setShowDetailsModal(true);
    };

    const exportToExcel = () => {
        const worksheetData = filtered.map(survey => {
            const surveyQuestions = (survey.questions || {}) as Record<string, string>;
            const hazardFromQuestions = surveyQuestions.fuelStationAvailable === 'Yes' || surveyQuestions.otherAssessmentHazardous === 'Yes';
            const hazardFromDetails = typeof survey.hazardousArea === 'string' && survey.hazardousArea.trim() !== '';
            const hazardFlag = survey.isHazardous ?? (hazardFromQuestions || hazardFromDetails);
            return {
                'Survey ID': survey.surveyId || 'N/A',
                'Address': survey.locationAddress || 'N/A',
                'Survey Request Date': survey.surveyRequestDate || 'N/A',
                'Survey Date': survey.surveyDate || 'N/A',
                'Survey Submitted On': survey.surveySubmittedOn || 'N/A',
                'Space Consists Of': survey.spaceConsistsOf || 'N/A',
                'Hazardous': hazardFlag ? 'Yes' : 'No',
                'Hazardous Details': survey.hazardousArea || 'N/A',
                'Directions': survey.directions ? Object.entries(survey.directions).map(([key, value]) => `${key}: ${value}`).join(', ') : 'N/A',
                'Questions': survey.questions ? Object.entries(survey.questions).map(([key, value]) => `${key}: ${value}`).join('; ') : 'N/A',
                'Remarks': survey.remarks || 'N/A',
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Location Surveys');

        const colWidths = [
            { wch: 20 }, // Survey ID
            { wch: 40 }, // Address
            { wch: 18 }, // Survey Request Date
            { wch: 15 }, // Survey Date
            { wch: 18 }, // Survey Submitted On
            { wch: 30 }, // Space Consists Of
            { wch: 12 }, // Hazardous
            { wch: 30 }, // Hazardous Details
            { wch: 40 }, // Directions
            { wch: 60 }, // Questions
            { wch: 40 }, // Remarks
        ];
        worksheet['!cols'] = colWidths;

        const fileName = `Location_Survey_Reports_${dateFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        showToast('Excel file exported successfully', 'success');
    };

    const exportToPDF = () => {
        if (filtered.length === 0) {
            showToast('No surveys to export', 'warning');
            return;
        }

        const doc = new jsPDF();
        let yPosition = 20;

        // Professional Header
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('ADVANCE GUARD SECURITY SERVICES', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(16);
        doc.setTextColor(52, 73, 94);
        doc.text('LOCATION SURVEY REPORTS', 20, yPosition);
        yPosition += 15;

        // Report Information
        doc.setFillColor(248, 249, 250);
        doc.rect(15, yPosition - 5, 180, 35, 'F');
        doc.setDrawColor(189, 195, 199);
        doc.rect(15, yPosition - 5, 180, 35, 'S');

        doc.setFontSize(10);
        doc.setTextColor(52, 73, 94);
        doc.setFont('helvetica', 'bold');
        doc.text('REPORT INFORMATION', 20, yPosition);
        yPosition += 6;

        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${dateFilter === 'all' ? 'All Time' : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Total Location Surveys: ${filtered.length}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPosition);
        yPosition += 15;

        // Table headers
        const headers = ['Survey ID', 'Address', 'Survey Date', 'Space Type', 'Hazardous'];
        const columnWidths = [30, 70, 25, 35, 20];
        const startX = 15;

        // Table header
        doc.setFillColor(52, 152, 219);
        doc.rect(startX, yPosition - 3, columnWidths.reduce((a, b) => a + b, 0), 8, 'F');

        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        let xPosition = startX + 2;
        headers.forEach((header, index) => {
            doc.text(header, xPosition, yPosition);
            xPosition += columnWidths[index];
        });
        yPosition += 10;

        // Table data
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);

        filtered.forEach((survey, index) => {
            if (yPosition > 270) {
                doc.addPage();
                yPosition = 20;
            }

            if (index % 2 === 0) {
                doc.setFillColor(248, 249, 250);
                doc.rect(startX, yPosition - 3, columnWidths.reduce((a, b) => a + b, 0), 6, 'F');
            }

            const surveyQuestions = (survey.questions || {}) as Record<string, string>;
            const hazardFromQuestionsPdf = (survey.questions as any)?.fuelStationAvailable === 'Yes' || (survey.questions as any)?.otherAssessmentHazardous === 'Yes';
            const hazardFromDetailsPdf = typeof survey.hazardousArea === 'string' && survey.hazardousArea.trim() !== '';
            const hazardFlagPdf = survey.isHazardous ?? (hazardFromQuestionsPdf || hazardFromDetailsPdf);
            const rowData = [
                survey.surveyId || 'N/A',
                survey.locationAddress || 'N/A',
                survey.surveyDate || 'N/A',
                survey.spaceConsistsOf || 'N/A',
                hazardFlagPdf ? 'Yes' : 'No'
            ];

            doc.setTextColor(44, 62, 80);
            xPosition = startX + 2;
            rowData.forEach((data, colIndex) => {
                const text = data.toString().substring(0, columnWidths[colIndex] / 1.5);
                doc.text(text, xPosition, yPosition);
                xPosition += columnWidths[colIndex];
            });
            yPosition += 6;
        });

        // Footer
        yPosition += 10;
        doc.setFontSize(8);
        doc.setTextColor(127, 140, 141);
        doc.setFont('helvetica', 'italic');
        doc.text('Generated by Advance Guard Security Management System', 20, yPosition);

        const fileName = `Location_Survey_Reports_${dateFilter}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        showToast('PDF file exported successfully', 'success');
    };

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    const columns: Column<NewLocationSurvey>[] = [
        { header: 'Survey ID', accessor: 'surveyId', width: '10%', render: (survey) => <span className="font-medium text-gray-900 dark:text-gray-100">{survey.surveyId}</span> },
        { header: 'Address', accessor: 'locationAddress', width: '20%', render: (survey) => <span className="text-gray-600 dark:text-gray-400">{survey.locationAddress}</span> },
        { header: 'Request Date', accessor: 'surveyRequestDate', width: '12%', render: (survey) => <span className="text-gray-600 dark:text-gray-400">{survey.surveyRequestDate}</span> },
        { header: 'Survey Date', accessor: 'surveyDate', width: '12%', render: (survey) => <span className="text-gray-600 dark:text-gray-400">{survey.surveyDate}</span> },
        { header: 'Space Type', accessor: 'spaceConsistsOf', width: '10%', render: (survey) => <span className="text-gray-900 dark:text-gray-100">{survey.spaceConsistsOf}</span> },
        {
            header: 'Hazardous',
            width: '12%',
            render: (survey) => {
                const surveyQuestions = (survey.questions || {}) as Record<string, string>;
                const hazardFromQuestionsRow = surveyQuestions.fuelStationAvailable === 'Yes' || surveyQuestions.otherAssessmentHazardous === 'Yes';
                const hazardFromDetailsRow = typeof survey.hazardousArea === 'string' && survey.hazardousArea.trim() !== '';
                const hazardFlag = survey.isHazardous ?? (hazardFromQuestionsRow || hazardFromDetailsRow);

                return (
                    <div className="space-y-2">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${hazardFlag
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            }`}>
                            {hazardFlag ? 'Yes' : 'No'}
                        </span>
                        {hazardFlag && (survey.hazardousArea?.trim() || '').length > 0 && (
                            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-800/40 dark:text-gray-300">
                                {survey.hazardousArea}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Status',
            width: '10%',
            render: (survey) => {
                const computedStatusLabel = survey.status || (survey.assignedTo && survey.assignedTo !== survey.createdBy ? 'Assigned' : 'Completed');
                const normalizedStatus = computedStatusLabel.toLowerCase();
                const isAssigned = normalizedStatus === 'assigned';

                return (
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${isAssigned
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300'
                        : normalizedStatus === 'completed'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                        }`}>
                        {computedStatusLabel}
                    </span>
                );
            }
        }
    ];

    if (isMIS || isRegional) {
        columns.push({ header: 'Assigned PSO', accessor: 'assignedToName', width: '15%', render: (survey) => <span className="text-gray-900 dark:text-gray-100">{survey.assignedToName || '—'}</span> });
    }

    columns.push({
        header: 'Actions',
        align: 'right',
        width: '10%',
        render: (survey) => {
            const computedStatusLabel = survey.status || (survey.assignedTo && survey.assignedTo !== survey.createdBy ? 'Assigned' : 'Completed');
            const normalizedStatus = computedStatusLabel.toLowerCase();
            const isAssigned = normalizedStatus === 'assigned';
            const showSubmitRemaining = isAssigned && survey.assignedTo === uid;

            return (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="secondary"
                        className="!px-3 !py-1.5 text-xs"
                        onClick={() => openDetailsModal(survey, false)}
                    >
                        <Icon icon={FiEye} className="mr-1 h-3 w-3" />
                        View
                    </Button>
                    {showSubmitRemaining && (
                        <Button
                            variant="primary"
                            className="!px-3 !py-1.5 text-xs"
                            onClick={() => openDetailsModal(survey, true)}
                        >
                            Submit Details
                        </Button>
                    )}
                </div>
            );
        }
    });

    return (
        <>
            <div className="space-y-6 p-6">
                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id || Math.random().toString()}
                    loading={false} // Loading handled by subscription or initial empty state? Original didn't have loading state variable.
                    emptyMessage={searchTerm || dateFilter !== 'all' ? 'Try adjusting your search or filter' : 'Get started by creating your first survey'}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                    title="Location Surveys"
                    description="View and manage all your location survey reports"
                    headerActions={
                        isMIS && (
                            <Button
                                onClick={() => setShowAddModal(true)}
                                className="flex items-center gap-2"
                            >
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Add Survey
                            </Button>
                        )
                    }
                    filters={
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center w-full">
                            <div className="flex-1">
                                <TextField
                                    type="text"
                                    placeholder="Search by Survey ID, Date, or Address..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    prefix={<Icon icon={FiSearch} />}
                                />
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <div className="w-full sm:w-48">
                                    <select
                                        value={dateFilter}
                                        onChange={(e) => setDateFilter(e.target.value as 'all' | 'daily' | 'weekly' | 'monthly')}
                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-4 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                    >
                                        <option value="all">All Time</option>
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                </div>
                                <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                                    <Icon icon={FiFile} className="h-4 w-4" />
                                    Export Excel
                                </Button>
                                <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                                    <Icon icon={FiDownload} className="h-4 w-4" />
                                    Export PDF
                                </Button>
                            </div>
                        </div>
                    }
                />
            </div>

            {/* Toast */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}

            {/* Add Survey Modal */}
            <AddNewLocationSurveyModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleSurveyAdded}
            />

            {/* Details Modal */}
            <NewLocationSurveyDetailsModal
                isOpen={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                survey={selectedSurvey}
                onSuccess={handleSurveyAdded}
                autoEdit={detailsAutoEdit}
            />
        </>
    );
}
